<script setup>
import '@campact/camper/core/CmprButton';
import '@campact/camper/core/CmprFormHint';
import '@campact/camper/core/CmprResultMessage';
import '@campact/camper/cta/CmprPrefilledArea';
import '@campact/camper/icons/CmprIconSolidCheck';

import { computed, inject } from 'vue';
import logoutAndRedirect from '@cta/frontend/javascripts/logout';
import StepItem from '@cta/donate_call_component/components/StepItem.vue';
import CmprCheckbox from '@cta/frontend/components/CmprCheckbox.vue';
import CmprInput from '@cta/frontend/components/CmprInput.vue';
import CmprSelect from '@cta/frontend/components/CmprSelect.vue';

const store = inject('store');

const props = defineProps({
  salutations: {
    type: Array,
    default() {
      return [];
    },
  },
  countryCodes: {
    type: Object,
    default() {
      return {};
    },
  },
  regions: {
    type: Array,
    default() {
      return [];
    },
  },
  prefilledAttributes: {
    type: Object,
    default() {
      return {
        salutation: undefined,
        firstname: undefined,
        lastname: undefined,
        countrycode: undefined,
        postalcode: undefined,
        region: undefined,
        locality: undefined,
        street: undefined,
        emailaddress: undefined,
      };
    },
  },
  collectAddress: {
    type: Boolean,
    default: true,
  },
  collectMobileNumber: {
    type: Boolean,
    default: false,
  },
  mobileNumberDisclaimer: {
    type: String,
    default: undefined,
  },
  mobileNumberRequired: {
    type: Boolean,
    default: false,
  },
  logoutPath: {
    type: String,
    default: undefined,
  },
  i18n: {
    type: Object,
    default: undefined,
  },
  signedIn: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['try-open', 'validate', 'commit']);

const inputsEmpty = computed(() => [store.values.firstname, store.values.lastname,
  store.values.street, store.values.locality, store.values.postalcode,
  store.values.region, store.values.emailaddress, store.values.mobile_number].every((value) => !value));

const title = computed(() => {
  if (inputsEmpty.value) return props.i18n.addressStepTitleEmpty;
  if (store.addressStepValid) return props.i18n.addressStepTitleComplete;
  return props.i18n.addressStepTitleIncomplete;
});
const summaryTitle = computed(() => [
  props.collectAddress && store.values.salutation !== 'Hallo' ? store.values.salutation : undefined,
  store.values.firstname,
  store.values.lastname,
].filter((value) => !!value).join(' '));
const summaryBody = computed(() => [
  props.collectAddress ? store.values.street : undefined, props.collectAddress ? store.values.countrycode : undefined,
  [
    props.collectAddress ? (store.values.countrycode == 'RO' ? store.values.region : store.values.postalcode) : undefined,
    props.collectAddress ? store.values.locality : undefined,
  ].filter((value) => !!value).join(' '),
  store.values.emailaddress,
  props.collectMobileNumber ? store.values.mobile_number : undefined,
].filter((value) => !!value).join(' · '));

function showErrorMessage(attribute) {
  return store.addressErrorUnlocked && store.errors[attribute];
}

function validate(attribute) {
  if (showErrorMessage(attribute)) emit('validate');
}
</script>

<template>
  <div class="address-step">
    <StepItem
      :number="2"
      :opened="store.addressStepOpen"
      :title="title"
      @try-open="emit('try-open')"
    >
      <template #opened>
        <div class="address-step__body">
          <a
            v-if="store.addressPrefilled"
            class="address-step__logout-link"
            @click="logoutAndRedirect(logoutPath)"
          >
            {{ i18n.logoutLinkText }}
          </a>

          <div class="address-step-input-grid">
            <div
              v-if="collectAddress"
              class="address-step-input-grid__column-one"
            >
              <CmprSelect
                v-model="store.values.salutation"
                :aria-label="i18n.attributes.salutation"
                autocomplete="honorific-prefix"
                class="address-step__salutation"
                data-analytics-context="address-step"
                data-analytics-id="form-call-form-contact-details-salutation-dropdown"
                data-analytics-inner-text="salutation"
              >
                <option
                  v-for="element in salutations"
                  :key="element"
                  :value="element"
                >
                  {{ element }}
                </option>
              </CmprSelect>
            </div>

            <div
              class="address-step-input-grid__column-one"
            >
              <CmprInput
                v-model="store.values.firstname"
                autocomplete="given-name"
                class="address-step__firstname"
                :class="{'is-invalid': showErrorMessage('firstname')}"
                data-analytics-context="address-step"
                data-analytics-id="form-call-form-contact-details-firstname-field"
                data-analytics-inner-text="firstname"
                :placeholder="i18n.attributes.firstname"
                @input="validate('firstname')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('firstname')"
                variant="error"
              >
                {{ store.errors.firstname }}
              </cmpr-form-hint>
            </div>

            <div
              class="address-step-input-grid__column-two"
            >
              <CmprInput
                v-model="store.values.lastname"
                autocomplete="family-name"
                class="address-step__lastname"
                :class="{'is-invalid': showErrorMessage('lastname')}"
                data-analytics-context="address-step"
                data-analytics-id="form-call-form-contact-details-lastname-field"
                data-analytics-inner-text="lastname"
                :placeholder="i18n.attributes.lastname"
                @input="validate('lastname')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('lastname')"
                variant="error"
              >
                {{ store.errors.lastname }}
              </cmpr-form-hint>
            </div>

            <div
              v-if="collectAddress"
              class="address-step-input-grid__column-span"
            >
              <CmprInput
                v-model="store.values.street"
                autocomplete="street-address"
                class="address-step__street"
                :class="{'is-invalid': showErrorMessage('street')}"
                data-analytics-context="address-step"
                data-analytics-id="form-call-form-contact-details-street-field"
                data-analytics-inner-text="street"
                :placeholder="i18n.attributes.street"
                @input="validate('street')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('street')"
                variant="error"
              >
                {{ store.errors.street }}
              </cmpr-form-hint>
            </div>

            <div
              v-if="collectAddress"
              class="address-step-input-grid__column-one"
            >
              <CmprSelect
                v-model="store.values.countrycode"
                :aria-label="i18n.attributes.countrycode"
                autocomplete="country"
                class="address-step__countrycode"
                data-analytics-context="address-step"
                data-analytics-id="form-call-form-contact-details-countrycode-dropdown"
                data-analytics-inner-text="countrycode"
              >
                <option
                  v-for="(code, name) in countryCodes"
                  :key="code"
                  :value="code"
                >
                  {{ name }}
                </option>
              </CmprSelect>
            </div>

            <div
              v-if="collectAddress && store.values.countrycode != 'RO'"
              class="address-step-input-grid__column-two"
            >
              <CmprInput
                v-model="store.values.postalcode"
                autocomplete="postal-code"
                class="address-step__postalcode"
                :class="{'is-invalid': showErrorMessage('postalcode')}"
                data-analytics-context="address-step"
                data-analytics-id="form-call-form-contact-details-postalcode-field"
                data-analytics-inner-text="postalcode"
                inputmode="numeric"
                :placeholder="i18n.attributes.postalcode"
                @input="validate('postalcode')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('postalcode')"
                variant="error"
              >
                {{ store.errors.postalcode }}
              </cmpr-form-hint>
            </div>

            <div
              v-if="collectAddress && store.values.countrycode == 'RO'"
              class="address-step-input-grid__column-two"
            >
              <CmprSelect
                v-model="store.values.region"
                :aria-label="i18n.attributes.region"
                autocomplete="address-level1"
                class="address-step__region"
                :class="{'is-invalid': showErrorMessage('region')}"
                data-analytics-context="address-step"
                data-analytics-id="form-call-form-contact-details-region-field"
                data-analytics-inner-text="region"
                @change="validate('region')"
              >
                <option
                  disabled="disabled"
                  :value="undefined"
                >
                  {{ i18n.selectPrompt }}
                </option>
                <option
                  v-for="(name) in regions"
                  :key="name"
                  :value="name"
                >
                  {{ name }}
                </option>
              </CmprSelect>

              <cmpr-form-hint
                v-if="showErrorMessage('region')"
                variant="error"
              >
                {{ store.errors.region }}
              </cmpr-form-hint>
            </div>

            <div
              v-if="collectAddress"
              class="address-step-input-grid__column-span"
            >
              <CmprInput
                v-model="store.values.locality"
                autocomplete="address-level2"
                class="address-step__locality"
                :class="{'is-invalid': showErrorMessage('locality')}"
                data-analytics-context="address-step"
                data-analytics-id="form-call-form-contact-details-locality-field"
                data-analytics-inner-text="locality"
                :placeholder="i18n.attributes.locality"
                @input="validate('locality')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('locality')"
                variant="error"
              >
                {{ store.errors.locality }}
              </cmpr-form-hint>
            </div>

            <div
              v-if="collectAddress && signedIn"
              class="address-step-input-grid__column-span cmpr-checkbox-control"
            >
              <CmprCheckbox
                id="opt-out-account-update-checkbox"
                v-model="store.values.optOutAccountUpdate"
                data-analytics-context="address-step"
                data-analytics-id="form-call-opt-out-account-update-checkbox"
                value="1"
              />
              <label
                class="cmpr-checkbox-control__label"
                for="opt-out-account-update-checkbox"
              >
                {{ i18n.attributes.opt_out_account_update }}
              </label>
            </div>

            <div
              v-if="collectMobileNumber"
              class="address-step-input-grid__column-span"
            >
              <!-- eslint-disable vue/no-v-text-v-html-on-component vue/no-v-html -->
              <cmpr-result-message
                class="mb-60"
                size="small"
                variant="information"
                v-html="mobileNumberDisclaimer"
              />
              <!-- eslint-enable vue/no-v-text-v-html-on-component vue/no-v-html -->

              <CmprInput
                v-model="store.values.mobile_number"
                autocomplete="tel"
                class="address-step__mobile-number"
                :class="{'is-invalid': showErrorMessage('mobile_number')}"
                data-analytics-context="address-step"
                data-analytics-id="form-call-form-contact-details-mobile-number-field"
                data-analytics-inner-text="mobilenumber"
                :disabled="!!prefilledAttributes['mobile_number']"
                :placeholder="[mobileNumberRequired
                  ? i18n.attributes.mobile_number
                  : i18n.attributes.optional_mobile_number]"
                type="tel"
                @input="validate('mobile_number')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('mobile_number')"
                variant="error"
              >
                {{ store.errors.mobile_number }}
              </cmpr-form-hint>
            </div>

            <div
              class="address-step-input-grid__column-span"
            >
              <CmprInput
                v-model="store.values.emailaddress"
                autocomplete="email"
                class="address-step__emailaddress"
                :class="{'is-invalid': showErrorMessage('emailaddress')}"
                data-analytics-context="address-step"
                data-analytics-id="form-call-form-contact-details-emailaddress-field"
                data-analytics-inner-text="emailaddress"
                :disabled="!!prefilledAttributes['emailaddress']"
                :placeholder="i18n.attributes.emailaddress"
                @input="validate('emailaddress')"
                @keydown.enter="emit('commit')"
              />

              <cmpr-form-hint
                v-if="showErrorMessage('emailaddress')"
                variant="error"
              >
                {{ store.errors.emailaddress }}
              </cmpr-form-hint>
            </div>
          </div>
        </div>
      </template>

      <template #closed>
        <cmpr-prefilled-area
          v-if="!inputsEmpty"
          class="address-step__summary"
          editable="editable"
          :variant="store.addressStepValid ? 'success' : 'warning'"
        >
          <div
            class="fw-bold"
          >
            {{ summaryTitle }}
          </div>

          <span
            v-if="summaryBody"
          >
            {{ summaryBody }}
          </span>
        </cmpr-prefilled-area>
      </template>
    </StepItem>
  </div>
</template>

<style scoped lang="scss">
.address-step__body {
  display: flex;
  flex-direction: column;
  gap: var(--cmpr-space-60);
}

.address-step__logout-link {
  align-self: start;
  cursor: pointer;
  font-size: var(--cmpr-font-size-30);
  font-weight: var(--cmpr-font-weight-20);
  line-height: var(--cmpr-font-line-height-30);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.address-step-input-grid {
  display: grid;
  gap: var(--cmpr-space-60);
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-rows: min-content;

  &:empty {
    display: none;
  }
}

.address-step-input-grid__column-one {
  grid-column: 1;
}

.address-step-input-grid__column-two {
  grid-column: 2;
}

.address-step-input-grid__column-span {
  grid-column: span 2;
}
</style>
